import React, {useEffect} from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Box,
    Button,
    Text,
    Flex,
    Popover,
    PopoverContent,
    PopoverBody,
    PopoverTrigger,
    useDisclosure,
    VisuallyHidden,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

import useMultiSite from '@app/hooks/use-multi-site'
import {useLocation} from 'react-router-dom'
import {buildUrlWithLocale} from '@app/utils/url'

// Icons
import {ChevronDownIcon, ChevronUpIcon} from '@app/components/icons'

import LocaleText from '../locale-text'
import {useIntl} from 'react-intl'

/**
 * LocaleSelector component provides a dropdown menu for selecting the locale.
 * It utilizes Chakra UI's Menu components to display a list of supported locales,
 * allowing users to switch the language of the application.
 *
 * @param {Object} props - The component props.
 * @param {string} props.selectedLocale - The currently selected locale.
 * @param {string[]} props.locales - An array of all supported locale IDs.
 * @param {Function} props.onSelect - Callback function called when a locale is selected.
 * @returns {JSX.Element} The rendered component.
 */
const LocaleSelector = ({selectedLocale = '', locales = [], onSelect = () => {}}) => {
    const intl = useIntl()
    const location = useLocation()
    const {site, locale: currentLocale} = useMultiSite()
    const {onOpen, onClose, isOpen} = useDisclosure()
    const menuBtnRef = React.useRef(null)
    const firstLinkRef = React.useRef(null)
    const popoverContentRef = React.useRef(null)

    // Align locale selector accessibility to keep "popover popup" behavior only without specific aria-*
    useEffect(() => {
        if (menuBtnRef.current) {
            menuBtnRef.current.removeAttribute('aria-haspopup')
        }
        if (popoverContentRef.current) {
            popoverContentRef.current.removeAttribute('role')
        }
    }, [])

    return (
        <Popover
            placement="top-end"
            variant="footerLocaleMenu"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            initialFocusRef={firstLinkRef}
        >
            <PopoverTrigger>
                <Button ref={menuBtnRef} textStyle={'bodyBase2'} p="12px 0" whiteSpace={'normal'}>
                    <Flex alignItems="center" justifyContent="center">
                        <Text as="span">
                            {intl.formatMessage({
                                id: 'localeSelector.language',
                                defaultMessage: 'Language:'
                            })}{' '}
                            <LocaleText shortCode={selectedLocale} />
                        </Text>
                        {isOpen ? (
                            <ChevronUpIcon boxSize={6} color="grey" aria-hidden="true" />
                        ) : (
                            <ChevronDownIcon boxSize={6} color="grey" aria-hidden="true" />
                        )}
                    </Flex>
                </Button>
            </PopoverTrigger>
            <PopoverContent ref={popoverContentRef}>
                <PopoverBody>
                    <VisuallyHidden>
                        <Text>
                            {intl.formatMessage({
                                defaultMessage: 'Language selector',
                                id: 'locale_selector.title'
                            })}
                        </Text>
                    </VisuallyHidden>

                    <Box as="ul">
                        {locales
                            .filter((locale) => locale?.id !== selectedLocale)
                            .map((localeObject, index) => {
                                const locale = localeObject.id
                                const localeAlias = localeObject.alias
                                const isFirst = index === 0

                                return (
                                    <Box
                                        key={localeAlias}
                                        as="li"
                                        textAlign="center"
                                        listStyleType="none"
                                    >
                                        <Link
                                            ref={isFirst ? firstLinkRef : undefined}
                                            lang={locale}
                                            translate="no"
                                            variant="noUnderline"
                                            href={buildUrlWithLocale(
                                                location,
                                                site,
                                                currentLocale,
                                                localeObject
                                            )}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                onSelect(locale)
                                            }}
                                            display="block"
                                            py="12px"
                                            pl="2px"
                                        >
                                            <LocaleText shortCode={locale} />
                                        </Link>
                                    </Box>
                                )
                            })}
                    </Box>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

LocaleSelector.displayName = 'LocaleSelector'

LocaleSelector.propTypes = {
    /**
     * A complete list of all the locales supported. This array must have content.
     */
    locales: PropTypes.array.isRequired,
    /**
     * The current locales shortcode.
     */
    selectedLocale: PropTypes.string.isRequired,
    /**
     * Function called when a locale is selected.
     */
    onSelect: PropTypes.func
}

export default LocaleSelector
